import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Form, Modal, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";

import {
  createPartnerType,
  deletePartnerType,
  getPartnerTypes,
  updatePartnerType,
} from "../../services/partnerTypeService";
import { partnerTypeSchema } from "../../validators/partnerTypeValidator";

const PartnerType = () => {
  const [partnerTypes, setPartnerTypes] = useState([]);
  const [filteredPartnerTypes, setFilteredPartnerTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [partnerTypeToEdit, setPartnerTypeToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [partnerTypeToDelete, setPartnerTypeToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);

  useEffect(() => {
    loadPartnerTypes();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      const filtered = partnerTypes.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPartnerTypes(filtered);
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm, partnerTypes]);

  const loadPartnerTypes = async () => {
    setLoading(true);
    try {
      const { data } = await getPartnerTypes();
      setPartnerTypes(data.data);
      setFilteredPartnerTypes(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load Partner Types"
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (partnerTypeToEdit) {
      onUpdatePartnerType(payload, resetForm);
    } else {
      onCreatePartnerType(payload, resetForm);
    }
  };

  const onCreatePartnerType = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createPartnerType(payload);

      setPartnerTypes((prev) => [...prev, data.data]);

      toast.success("Partner Type created successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Partner Type was not created, try again!"
        );
      }
    }
  };

  const handleEdit = (category) => {
    setPartnerTypeToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdatePartnerType = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalPartnerTypes = [...partnerTypes];

      const partnerTypeIndex = originalPartnerTypes.findIndex(
        (partnerType) => partnerType.id === partnerTypeToEdit.id
      );

      if (partnerTypeIndex === -1) throw new Error("Partner Type not found.");

      const { data } = await updatePartnerType(partnerTypeToEdit.id, payload);

      originalPartnerTypes[partnerTypeIndex] = data.data;

      setPartnerTypes(originalPartnerTypes);

      setPartnerTypeToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Partner Type updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Partner Type not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setPartnerTypeToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeletePartnerType = async () => {
    const originalPartnerTypes = [...partnerTypes];

    const newPartnerTypes = originalPartnerTypes.filter(
      (partnerType) => partnerType.id !== partnerTypeToDelete.id
    );

    setPartnerTypes(newPartnerTypes);

    try {
      await deletePartnerType(partnerTypeToDelete?.id);

      toast.success("Partner Type deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message || error?.response?.data?.error || "Partner Type not deleted, try again later"
        }`
      );

      setPartnerTypes(originalPartnerTypes);
    } finally {
      setIsDeleteModalOpen(false);

      setPartnerTypeToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setPartnerTypeToEdit(null);
    setPartnerTypeToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 200,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "left", color: "#888" }}>{displayText}</div>
        ) : (
          <Tooltip title={text}>
            <div
              style={{
                maxWidth: 250,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {text}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Partner Type">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Partner Type">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Partner Types" subtitle1="Partner Types" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Partner Type List"
            count={filteredPartnerTypes.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Partner Type"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredPartnerTypes.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {partnerTypeToEdit ? "Edit Partner Type" : "Add Partner Type"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                name: partnerTypeToEdit ? partnerTypeToEdit?.name : "",
                description: partnerTypeToEdit
                  ? partnerTypeToEdit?.description
                  : "",
              }}
              enableReinitialize
              validationSchema={partnerTypeSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({ values, handleChange, handleBlur, errors, submitForm }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    <TextInput
                      name="name"
                      title="Name"
                      placeholder="Enter Partner Type"
                      requiredField={true}
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      autoFocus
                    />

                    <TextInput
                      name="description"
                      title="Description"
                      placeholder="Enter Partner Type Description"
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeletePartnerType}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default PartnerType;
